<template>
    <DCard
        padding
    >
        <DFormRow
            :label="__('settings.affiliate_settings.form.sync_type')"
            :is-required="true"
            :error="errors.sync_type"
            :hint="__('settings.affiliate_settings.form.sync_type_hint')"
        >
            <DSelect
                id="sync-type"
                v-model="form.sync_type"
                :options="syncTypes"
                :is-valid="isFormFieldValid('sync_type')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.affiliate_settings.form.api_uri')"
            :is-required="true"
            :error="errors.api_uri"
            :hint="__('settings.affiliate_settings.form.api_uri_hint')"
        >
            <DInput
                id="api-url"
                v-model="form.api_uri"
                :is-valid="isFormFieldValid('api_uri')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'api'"
            :label="__('settings.affiliate_settings.form.api_token')"
            :is-required="true"
            :error="errors.api_token"
            :hint="__('settings.affiliate_settings.form.api_token_hint')"
        >
            <DInput
                id="api-token"
                v-model="form.api_token"
                :is-valid="isFormFieldValid('api_token')"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'api'"
            :label="__('settings.affiliate_settings.form.user_id')"
            :is-required="true"
            :error="errors.user_id"
            :hint="__('settings.affiliate_settings.form.user_id_hint')"
        >
            <DInput
                id="user-id"
                v-model="form.user_id"
                :is-valid="isFormFieldValid('user_id')"
            />
        </DFormRow>
        <DRow
            v-if="form.sync_type === 'api'"
            class="d-form-row mb-4"
        >
            <DCol sm="4" />
            <DCol sm="8">
                <div
                    v-if="connectionSuccessful === true"
                    class="green-text d-inline-block"
                >
                    {{ __('settings.affiliate_settings.connection_successful') }}
                </div>
                <div
                    v-if="connectionSuccessful === false"
                    class="red-text d-inline-block"
                >
                    {{ __('settings.affiliate_settings.connection_unsuccessful') }}
                </div>
                <div class="text-right">
                    <DButton
                        class="d-inline-block float-end"
                        icon="lan-connect"
                        :label="__('settings.affiliate_settings.check_connection')"
                        :disabled="isInLoadingState"
                        @click="checkConnection"
                    />
                </div>
            </DCol>
        </DRow>
        <DFormRow
            :label="__('settings.affiliate_settings.form.campaign_id')"
            :is-required="true"
            :error="errors.campaign_id"
            :hint="__('settings.affiliate_settings.form.campaign_id_hint')"
        >
            <DInput
                id="campaign-id"
                v-model="form.campaign_id"
                :is-valid="isFormFieldValid('campaign_id')"
                maxlength="10"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'ftp'"
            :label="__('settings.affiliate_settings.form.sftp_host')"
            :is-required="true"
            :error="errors.sftp_host"
            :hint="__('settings.affiliate_settings.form.sftp_host_hint')"
        >
            <DInput
                id="sftp-host"
                v-model="form.sftp_host"
                :is-valid="isFormFieldValid('sftp_host')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'ftp'"
            :label="__('settings.affiliate_settings.form.sftp_port')"
            :is-required="true"
            :error="errors.sftp_port"
            :hint="__('settings.affiliate_settings.form.sftp_port_hint')"
        >
            <DInput
                id="sftp-port"
                v-model="form.sftp_port"
                :is-valid="isFormFieldValid('sftp_port')"
                min="0"
                max="65535"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'ftp'"
            :label="__('settings.affiliate_settings.form.sftp_username')"
            :is-required="true"
            :error="errors.sftp_username"
            :hint="__('settings.affiliate_settings.form.sftp_username_hint')"
        >
            <DInput
                id="sftp-username"
                v-model="form.sftp_username"
                :is-valid="isFormFieldValid('sftp_username')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'ftp'"
            :label="__('settings.affiliate_settings.form.sftp_password')"
            :is-required="true"
            :error="errors.sftp_password"
            :hint="__('settings.affiliate_settings.form.sftp_password_hint')"
        >
            <DInput
                id="sftp-password"
                v-model="form.sftp_password"
                :is-valid="isFormFieldValid('sftp_password')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            v-if="form.sync_type === 'ftp'"
            :label="__('settings.affiliate_settings.form.sftp_root_dir')"
            :is-required="true"
            :error="errors.sftp_root_dir"
            :hint="__('settings.affiliate_settings.form.sftp_root_dir_hint')"
        >
            <DInput
                id="sftp-root-dir"
                v-model="form.sftp_root_dir"
                :is-valid="isFormFieldValid('sftp_root_dir')"
                maxlength="255"
            />
        </DFormRow>
        <DFormControls
            :dirty="form.isDirty"
            @submit="saveSettings"
        />
        <DConnectionErrorModal
            :show-modal="connectionException !== null"
            :exception="connectionException"
            @close-modal="closeErrorCheckModal"
        />
    </DCard>
</template>

<script setup>
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'

import { ref } from 'vue'
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    settings: {
        type: Object,
        default () {
            return {}
        }
    },
    syncTypes: {
        type: Array,
        default () {
            return []
        }
    },
    errors: {
        type: Object,
        default () {
            return {}
        }
    }
})

const form = useForm({
    sync_type: props.settings?.sync_type,
    api_uri: props.settings?.api_uri,
    api_token: '',
    user_id: props.settings?.user_id,
    campaign_id: props.settings?.campaign_id,
    sftp_host: props.settings?.sftp_host,
    sftp_port: props.settings?.sftp_port,
    sftp_username: props.settings?.sftp_username,
    sftp_password: '',
    sftp_root_dir: props.settings?.sftp_root_dir
})

const connectionException = ref(null)
const saveSettings = () => {
    form.post(route('settings.affiliate-settings.store'))
}

// Check connection
const connectionSuccessful = ref(null)
const checkConnection = () => {
    connectionException.value = null
    window.axios.post(route('settings.affiliate-settings.check', form))
        .then(function () {
            connectionSuccessful.value = true
        })
        .catch(function (error) {
            connectionSuccessful.value = false
            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

const closeErrorCheckModal = () => {
    connectionException.value = null
}
</script>

<style scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 25px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
